import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { DashPanel } from '../components/DashPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAnalysis } from '../context/AnalysisContext';
import { useEvents } from '../context/EventsContext';
import type { AnalysisType, ResearchObjective } from '../types/analysis';
import { ANALYSIS_TYPE_LABELS, MIN_EVENTS_FOR_ANALYSIS, RESEARCH_OBJECTIVE_LABELS } from '../types/analysis';

function emptyState() {
  return {
    title: '',
    types: [] as AnalysisType[],
    customType: '',
    objectives: [] as ResearchObjective[],
    eventIds: [] as string[],
  };
}

/** Mesma estrutura de página do EventFormPage — header com voltar, formulário
 * num DashPanel, ações fora do card. Antes era um Dialog; agora é uma página
 * própria pra ficar no mesmo estilo de "criar evento". */
export default function AnalysisFormPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const { getAnalysisById, createAnalysis, updateAnalysis } = useAnalysis();
  const { events } = useEvents();
  const navigate = useNavigate();

  const analysis = analysisId ? getAnalysisById(analysisId) : undefined;
  const isEdit = Boolean(analysisId);

  const [state, setState] = useState(() =>
    analysis
      ? {
          title: analysis.title,
          types: analysis.types,
          customType: analysis.customTypeLabel ?? '',
          objectives: analysis.researchObjectives,
          eventIds: analysis.eventIds,
        }
      : emptyState()
  );

  useEffect(() => {
    if (isEdit && !analysis) navigate('/dashboard/analysis', { replace: true });
  }, [isEdit, analysis, navigate]);

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

  // "Todos (visão geral)" usa todos os eventos criados; os outros tipos usam só os vinculados.
  const isGeral = state.types.includes('geral');
  const eventsInScope = isGeral ? events.length : state.eventIds.length;
  const hasEnoughEvents = eventsInScope >= MIN_EVENTS_FOR_ANALYSIS;
  const canSubmit = state.types.length > 0 && hasEnoughEvents;

  const backTo = isEdit && analysis ? `/dashboard/analysis/${analysis.id}` : '/dashboard/analysis';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const input = {
      title: state.title,
      types: state.types,
      customTypeLabel: state.types.includes('outro') ? state.customType : undefined,
      researchObjectives: state.objectives,
      eventIds: state.eventIds,
      collaboratorEmails: [] as string[],
    };

    if (isEdit && analysis) {
      updateAnalysis(analysis.id, input);
      navigate(`/dashboard/analysis/${analysis.id}`);
      return;
    }

    const created = createAnalysis(input);
    navigate(`/dashboard/analysis/${created.id}`);
  };

  return (
    <DashboardShell mode="analysis">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(backTo)}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar
          </Button>
          <div>
            <h1 className="text-base font-semibold text-foreground">
              {isEdit ? 'Editar análise' : 'Criar análise de eventos'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Vincule eventos, defina os objetivos de pesquisa e os métodos de análise.
            </p>
          </div>
        </div>
        <UserMenu />
      </header>

      <div className="flex-1 overflow-y-auto p-6 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <form onSubmit={handleSubmit} className="max-w-2xl">
        <DashPanel className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="an-title">Título</Label>
            <Input
              id="an-title"
              required
              placeholder="Ex: Evolução de comunicação — Visitas semanais"
              value={state.title}
              onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label>
              Objetivos de pesquisa <span className="text-muted-foreground font-normal">(múltipla escolha)</span>
            </Label>
            <div className="max-h-40 overflow-y-auto border border-border rounded-[10px] p-2 space-y-0.5">
              {Object.entries(RESEARCH_OBJECTIVE_LABELS).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer">
                  <Checkbox
                    checked={state.objectives.includes(k as ResearchObjective)}
                    onCheckedChange={() =>
                      setState((prev) => ({ ...prev, objectives: toggle(prev.objectives, k as ResearchObjective) }))
                    }
                  />
                  <span className="text-sm text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Métodos estatísticos <span className="text-muted-foreground font-normal">(múltipla escolha)</span>
            </Label>
            <div className="max-h-40 overflow-y-auto border border-border rounded-[10px] p-2 space-y-0.5">
              {Object.entries(ANALYSIS_TYPE_LABELS).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer">
                  <Checkbox
                    checked={state.types.includes(k as AnalysisType)}
                    onCheckedChange={() =>
                      setState((prev) => ({ ...prev, types: toggle(prev.types, k as AnalysisType) }))
                    }
                  />
                  <span className="text-sm text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {state.types.includes('outro') && (
            <div className="space-y-2">
              <Label htmlFor="an-custom">Especifique o método</Label>
              <Input
                id="an-custom"
                required
                value={state.customType}
                onChange={(e) => setState((prev) => ({ ...prev, customType: e.target.value }))}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Eventos vinculados <span className="text-muted-foreground font-normal">(mínimo {MIN_EVENTS_FOR_ANALYSIS})</span>
            </Label>
            <div className="max-h-56 overflow-y-auto border border-border rounded-[10px] p-2 space-y-0.5">
              {events.map((ev) => (
                <label key={ev.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer">
                  <Checkbox
                    checked={state.eventIds.includes(ev.id)}
                    onCheckedChange={() =>
                      setState((prev) => ({ ...prev, eventIds: toggle(prev.eventIds, ev.id) }))
                    }
                  />
                  <span className="text-sm text-foreground">{ev.title}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{ev.school}</span>
                </label>
              ))}
            </div>
            {!hasEnoughEvents && (
              <p className="text-xs text-muted-foreground">
                {isGeral
                  ? `A análise precisa de pelo menos ${MIN_EVENTS_FOR_ANALYSIS} eventos criados para cruzar dados.`
                  : `Selecione pelo menos ${MIN_EVENTS_FOR_ANALYSIS} eventos para cruzar dados.`}
              </p>
            )}
          </div>
        </DashPanel>

          <div className="flex justify-end gap-2 pt-5">
            <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isEdit ? 'Salvar alterações' : 'Criar análise'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardShell>
  );
}
